// The standard manager card and grid shared by the Investigations, Forms,
// and Redactions list pages, so the three surfaces read as one system:
// a rounded primary-tinted icon tile on the left, and a content column
// beside it — title row (with optional right-aligned actions), an optional
// subtitle, an optional meta row of chips/counts, then anything else.
//
// Buttons passed via `actions` must stopPropagation themselves (the card
// itself is clickable); display-only chips there stay click-through.
import { ReactNode } from "react";
import cn from "classnames";

export const CardGrid: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn("grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3", className)}>{children}</div>
);

export const ItemCard: React.FC<{
  // The icon element itself; the caller sizes/colors it (h-6 w-6 text-primary).
  icon: ReactNode;
  title: ReactNode;
  // Right end of the title row: action buttons, status chips.
  actions?: ReactNode;
  // Single line under the title.
  subtitle?: ReactNode;
  // Bottom row: counts, badges, dates. Wraps.
  meta?: ReactNode;
  // Anything below the meta row (extra lines some cards need).
  children?: ReactNode;
  onClick?: () => void;
  // Processing/locked states: dimmed, no hover affordance, click ignored.
  disabled?: boolean;
  // One-shot attention flash after creation (Forms).
  highlighted?: boolean;
  className?: string;
}> = ({ icon, title, actions, subtitle, meta, children, onClick, disabled, highlighted, className }) => (
  <div
    onClick={disabled ? undefined : onClick}
    className={cn(
      "p-4 border rounded-lg bg-base-100 transition-all group",
      highlighted ? "animate-highlight-fade border-blue-400" : "border-base-300",
      disabled
        ? "opacity-80 cursor-default"
        : onClick && "hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md cursor-pointer",
      className
    )}
  >
    <div className="flex items-start gap-3">
      <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3
            className={cn(
              "font-medium text-sm line-clamp-1 min-w-0",
              !disabled && onClick && "group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors"
            )}
          >
            {title}
          </h3>
          {actions && <span className="shrink-0 inline-flex items-center gap-1">{actions}</span>}
        </div>
        {subtitle && <p className="text-xs text-base-content/60 mt-0.5 line-clamp-2">{subtitle}</p>}
        {meta && <div className="flex items-center gap-2 mt-2 flex-wrap">{meta}</div>}
        {children}
      </div>
    </div>
  </div>
);

export default ItemCard;
